import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { apiClient, endpoints, unwrap } from '@core/api';
import { useAuthStore } from '@core/store/authStore';

const STORAGE_KEY = 'bytz_address_book_v1';
const newAddressId = () =>
  `addr_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

export const useAddressBookStore = create(
  persist(
    (set, get) => ({
      addresses: [],
      selectedAddressId: null,
      addressMetadata: {}, // Maps address_id to { fullName, phone }

      selectedAddress: () => {
        const { addresses, selectedAddressId } = get();
        return (
          addresses.find((address) => address.id === selectedAddressId) ?? addresses[0] ?? null
        );
      },

      fetchAddresses: async () => {
        const { customer, accessToken } = useAuthStore.getState();
        if (!accessToken) {
          return;
        }
        try {
          const raw = await apiClient.get(endpoints.account.deliveryAddress);
          const { data } = unwrap(raw);

          const rawList = Array.isArray(data) ? data : [];
          const mapped = rawList.map((addr) => {
            const metadata = get().addressMetadata[addr.address_id] || {};
            const fallbackName =
              `${customer?.first_name || ''} ${customer?.last_name || ''}`.trim() || 'Customer';
            const fallbackPhone = customer?.mobile?.number || '';

            return {
              id: addr.address_id,
              fullName: metadata.fullName || fallbackName,
              phone: metadata.phone || fallbackPhone,
              doorNo: addr.building_number || '',
              addressLine: addr.line || '',
              city: addr.city || '',
              state: addr.state || '',
              pincode: addr.zip_code || '',
              landmark: addr.land_mark || '',
              category: addr.type || 'Home',
              latitude: addr.coordinates?.latitude,
              longitude: addr.coordinates?.longitude,
              formattedAddress: addr.line || '',
            };
          });
          set({
            addresses: mapped,
            selectedAddressId: get().selectedAddressId || mapped[0]?.id || null,
          });
        } catch (error) {
          console.error('Failed to fetch addresses from API:', error);
        }
      },

      addAddress: async (input) => {
        const { accessToken } = useAuthStore.getState();
        if (!accessToken) {
          // Fallback to local guest add
          const address = { ...input, id: newAddressId() };
          set((state) => ({
            addresses: [...state.addresses, address],
            selectedAddressId: state.selectedAddressId ?? address.id,
          }));
          return;
        }

        try {
          const addressLine = input.doorNo + (input.landmark ? `, ${input.landmark}` : '');
          const payload = {
            building_number: input.doorNo || '',
            line: addressLine,
            city: input.city || '',
            state: input.state || '',
            zip_code: input.pincode || '',
            land_mark: input.landmark || '',
            type: input.category || 'Home',
            coordinates: {
              latitude: Number(input.latitude) || 0,
              longitude: Number(input.longitude) || 0,
            },
            country: input.country || 'IN',
            area: input.landmark || '',
            plot_identification_number: '',
          };

          const raw = await apiClient.post(endpoints.account.deliveryAddress, {
            data: payload,
          });
          const { data } = unwrap(raw);

          if (data && data.address_id) {
            // Save metadata
            const metadata = { ...get().addressMetadata };
            metadata[data.address_id] = {
              fullName: input.fullName || '',
              phone: input.phone || '',
            };
            set({ addressMetadata: metadata });
          }

          // Fetch updated addresses
          await get().fetchAddresses();
        } catch (error) {
          console.error('Failed to save address to API:', error);
        }
      },

      removeAddress: async (id) => {
        const { accessToken } = useAuthStore.getState();
        if (!accessToken) {
          // Fallback to local guest remove
          set((state) => {
            const newAddresses = state.addresses.filter((a) => a.id !== id);
            return {
              addresses: newAddresses,
              selectedAddressId:
                state.selectedAddressId === id
                  ? (newAddresses[0]?.id ?? null)
                  : state.selectedAddressId,
            };
          });
          return;
        }

        try {
          await apiClient.delete(endpoints.account.deliveryAddressById(id));

          // Remove metadata
          const metadata = { ...get().addressMetadata };

          delete metadata[id];
          set({ addressMetadata: metadata });

          // Fetch updated addresses
          await get().fetchAddresses();
        } catch (error) {
          console.error('Failed to delete address from API:', error);
        }
      },

      selectAddress: (id) => set({ selectedAddressId: id }),
      clear: () => set({ addresses: [], selectedAddressId: null, addressMetadata: {} }),
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
    },
  ),
);
